import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Carousel } from "bootstrap";
import { db } from "../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";

const IMAGE_INTERVAL = 3000;
const VIDEO_HOLD_INTERVAL = 999999;
const isVideoUrl = (u) => /\.mp4($|\?)/i.test(String(u || ""));

const FALLBACKS = [
  {
    bg: "linear-gradient(135deg,#E8F5E9 0%,#C8E6C9 100%)",
    title: "Bienvenue chez MAMECHE",
    subtitle: "Matériels médico-chirurgicaux, consommables & solutions industrielles.",
    primary: { label: "Département Médical", href: "/medical/products", variant: "success" },
    secondary: { label: "Département Industriel", href: "/industrial/products", variant: "outline-secondary" },
  },
  {
    bg: "linear-gradient(135deg,#E3F2FD 0%,#BBDEFB 100%)",
    title: "Solutions médicales & industrielles",
    subtitle: "Devis 24–48h • Livraison B2B • Support technique.",
    primary: { label: "Catalogue Médical", href: "/medical/products", variant: "success" },
    secondary: { label: "Catalogue Industriel", href: "/industrial/products", variant: "outline-dark" },
  },
  {
    bg: "linear-gradient(135deg,#FFF3E0 0%,#FFE0B2 100%)",
    title: "Qualité certifiée • CE / ISO*",
    subtitle: "Sélection rigoureuse des équipements, installation & SAV.",
    primary: { label: "Contact & Devis", href: "/contact", variant: "success" },
    secondary: { label: "Nos réalisations", href: "/about", variant: "outline-secondary" },
  },
];

const btnClass = (variant) => `btn btn-hero ${variant.startsWith("outline-") ? `btn-${variant}` : `btn-${variant}`}`;

// hook simple : desktop ou mobile
function useIsDesktop(bp = "(min-width: 768px)") {
  const [isDesk, setIsDesk] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(bp).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(bp);
    const onChange = (e) => setIsDesk(e.matches);
    mq.addEventListener?.("change", onChange);
    mq.addListener?.(onChange);
    return () => {
      mq.removeEventListener?.("change", onChange);
      mq.removeListener?.(onChange);
    };
  }, [bp]);
  return isDesk;
}

export default function HeroSection() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const isDesktop = useIsDesktop();

  const carouselRef = useRef(null);
  const videoRefs = useRef([]);    // <video> visibles (viewport courant)
  const [fitMap, setFitMap] = useState({}); // {index: "cover"|"contain"}

  // Charge les slides (ordre croissant si présent)
  useEffect(() => {
    const qy = query(collection(db, "homeSlides"), orderBy("order", "asc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setSlides(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // Bootstrap carousel + “ended” => next
  useEffect(() => {
    if (loading || !carouselRef.current) return;
    const el = carouselRef.current;
    const instance = Carousel.getInstance(el) || new Carousel(el, {
      // const instance = bootstrap.Carousel.getOrCreateInstance(carouselRef.current, {
      interval: IMAGE_INTERVAL,
      ride: "carousel",
      touch: true,
      pause: "hover",
      wrap: true,
      keyboard: true,
    });

    videoRefs.current.forEach((v) => {
      if (!v) return;
      const onEnd = () => instance.next();
      v.addEventListener("ended", onEnd);
      v.__onEnd = onEnd;
    });

    return () => {
      Carousel.getInstance(el)?.dispose();
      videoRefs.current.forEach((v) => v?.__onEnd && v.removeEventListener("ended", v.__onEnd));
    };
  }, [loading, slides, isDesktop]);

  // --- Décide cover/contain pour les images (selon ratio écran ↔ média)
  function decideImageFit(url, cb) {
    const viewportRatio = window.innerWidth / window.innerHeight;
    const img = new Image();
    img.onload = () => {
      const mediaRatio = img.naturalWidth / img.naturalHeight;
      const delta = Math.abs(Math.log(mediaRatio / viewportRatio));
      cb(delta > 0.35 ? "contain" : "cover");
    };
    img.src = url;
  }

  // Recalcul des fits images au chargement + au resize
  useEffect(() => {
    function recalc() {
      slides.forEach((s, i) => {
        const url = ((isDesktop ? s.src : s.mobile) || "").trim();
        if (url && !isVideoUrl(url)) {
          // Si l’admin a imposé un fit, on s’aligne
          if (s.fit === "cover" || s.fit === "contain") {
            setFitMap((m) => ({ ...m, [i]: s.fit }));
          } else {
            decideImageFit(url, (fit) => setFitMap((m) => ({ ...m, [i]: fit })));
          }
        }
      });
    }
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [slides, isDesktop]);

  // Vidéos : décide le fit au chargement des métadonnées
  const onVideoMeta = (i, forcedFit) => (e) => {
    if (forcedFit === "cover" || forcedFit === "contain") {
      setFitMap((m) => ({ ...m, [i]: forcedFit }));
      return;
    }
    const v = e.currentTarget;
    const mediaRatio = (v.videoWidth || 16) / (v.videoHeight || 9);
    const viewportRatio = window.innerWidth / window.innerHeight;
    const delta = Math.abs(Math.log(mediaRatio / viewportRatio));
    setFitMap((m) => ({ ...m, [i]: delta > 0.35 ? "contain" : "cover" }));
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="placeholder-glow">
          <span className="placeholder col-8" style={{ height: 300, display: "inline-block" }} />
        </div>
      </div>
    );
  }

  if (!slides.length) {
    const f = FALLBACKS[0];
    return (
      <div className="border-bottom" style={{ background: f.bg }}>
        <div className="container py-5 text-center">
          <h1 className="fw-bold display-5">{f.title}</h1>
          <p className="lead text-muted mt-3">{f.subtitle}</p>
          <div className="d-flex justify-content-center gap-2 mt-2 flex-wrap">
            <Link to={f.primary.href} className={btnClass(f.primary.variant)}>{f.primary.label}</Link>
            <Link to={f.secondary.href} className={btnClass(f.secondary.variant)}>{f.secondary.label}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="heroCarousel"
      className="carousel slide carousel-fade hero-no-gap hero-full"
      data-bs-ride="carousel"
      ref={carouselRef}
    >


      {slides.length > 1 && (
        <div className="carousel-indicators">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-bs-target="#heroCarousel"
              data-bs-slide-to={i}
              className={i === 0 ? "active" : ""}
              aria-label={`Slide ${i + 1}`}

            />
          ))}
        </div>
      )}

      <div className="carousel-inner w-100 h-100">
        {slides.map((slide, i) => {
          const active = i === 0 ? "active" : "";
          const deskUrl = (slide.src || "").trim();
          const mobUrl = (slide.mobile || "").trim();

          const currentUrl = isDesktop ? deskUrl : mobUrl;
          const hasCurrent = !!currentUrl;
          const currentIsVideo = hasCurrent && isVideoUrl(currentUrl);

          const fb = FALLBACKS[i % FALLBACKS.length];
          const forcedFit = slide.fit; // "cover" | "contain" (facultatif côté admin)
          const fit = forcedFit || fitMap[i] || "cover";

          // point focal (0–100), optionnel côté admin
          const fx = `${slide.focusX ?? 50}%`;
          const fy = `${slide.focusY ?? 50}%`;

          return (
            <div
              key={slide.id || i}
              className={`carousel-item h-100 ${active}`}
              data-bs-interval={currentIsVideo ? VIDEO_HOLD_INTERVAL : IMAGE_INTERVAL}
            >
              <div
                className={`hero-pane ${hasCurrent && !currentIsVideo ? "is-image" : ""} ${fit}`}
                style={
                  hasCurrent && !currentIsVideo
                    ? { backgroundImage: `url("${currentUrl}")`, "--fx": fx, "--fy": fy }
                    : !hasCurrent
                      ? { background: fb.bg }
                      : { "--fx": fx, "--fy": fy }
                }
              >
                {hasCurrent && currentIsVideo && (
                  <video
                    ref={(el) => (videoRefs.current[i] = el)}
                    className="hero-bg-video"
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={onVideoMeta(i, forcedFit)}
                    style={{ "--fx": fx, "--fy": fy }}
                  >
                    <source src={currentUrl} type="video/mp4" />
                  </video>
                )}

                {/* Pas d'overlay pour ne jamais "couvrir" le média */}

                {/* CTA/Texte uniquement si un média réel est rendu */}
                {hasCurrent && (slide.legend || (slide.cta && slide.link)) && (
                  <div className="hero-center text-white text-center">
                    {slide.legend && <h1 className="hero-title mb-3">{slide.legend}</h1>}
                    {slide.cta && slide.link && (
                      <Link to={slide.link} className="btn btn-success btn-cta">
                        <span className="btn-bridge__label">{slide.cta || "Explorer"}</span>
                        <span aria-hidden="true" className="btn-bridge__icon">→</span>
                      </Link>
                    )}
                  </div>
                )}

                {/* Fallback CTA quand aucun média pour ce viewport */}
                {!hasCurrent && (
                  <div className="hero-center text-center">
                    <h1 className="hero-title mb-2">{fb.title}</h1>
                    <p className="text-muted mb-3">{fb.subtitle}</p>
                    <div className="d-flex justify-content-center gap-2 flex-wrap">
                      <Link to={fb.primary.href} className={btnClass(fb.primary.variant)}>{fb.primary.label}</Link>
                      <Link to={fb.secondary.href} className={btnClass(fb.secondary.variant)}>{fb.secondary.label}</Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {slides.length > 1 && (
        <>
          <button className="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">Précédent</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">Suivant</span>
          </button>
        </>
      )}
    </div>
  );
}
